import { useCallback, useEffect, useRef, useState } from "react";

// Singleton worker so the model loads only once across the whole app
let workerInstance = null;
function getWorker() {
  if (!workerInstance) {
    workerInstance = new Worker(
      new URL("../workers/aiWorker.js", import.meta.url),
      { type: "module" }
    );
  }
  return workerInstance;
}

/**
 * useLocalAI — runs Qwen2.5-0.5B-Instruct in the browser via Transformers.js
 *
 * status: 'idle' | 'loading' | 'ready' | 'generating' | 'error'
 * progress: { file, progress, loaded, total } or null
 * device: 'webgpu' | 'wasm' | null
 */
export function useLocalAI() {
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(null);
  const [device, setDevice] = useState(null);
  const [error, setError] = useState(null);
  const [streamingText, setStreamingText] = useState("");
  const doneRef = useRef(null);

  useEffect(() => {
    const w = getWorker();
    const onMsg = (e) => {
      const { type, payload } = e.data;
      switch (type) {
        case "progress":
          setProgress(payload);
          if (status === "idle") setStatus("loading");
          break;
        case "ready":
          setStatus("ready");
          setDevice(payload.device);
          setProgress(null);
          break;
        case "token":
          setStreamingText((t) => t + payload);
          break;
        case "done":
          setStatus("ready");
          if (doneRef.current) {
            doneRef.current(payload.content || "");
            doneRef.current = null;
          }
          break;
        case "error":
          setStatus("error");
          setError(payload.message);
          if (doneRef.current) {
            doneRef.current("");
            doneRef.current = null;
          }
          break;
        default:
          break;
      }
    };
    w.addEventListener("message", onMsg);
    return () => w.removeEventListener("message", onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(() => {
    if (status === "loading" || status === "ready" || status === "generating") return;
    setStatus("loading");
    setError(null);
    getWorker().postMessage({ type: "load" });
  }, [status]);

  const generate = useCallback(
    async (messages, opts = {}) => {
      if (status === "generating") return "";
      setStreamingText("");
      setStatus("generating");
      setError(null);
      return new Promise((resolve) => {
        doneRef.current = resolve;
        getWorker().postMessage({
          type: "generate",
          payload: { messages, maxTokens: opts.maxTokens ?? 512 },
        });
      });
    },
    [status]
  );

  return { status, progress, device, error, streamingText, load, generate };
}
